/**
 * TextareaContentUtil - I read what the editor rendered and hand back plain text or HTML. ONLY
 * concerned with the rendered DOM - no editor state, no markdown, and no clipboard.
 *
 * **The HTML is read, not generated.** `ReadonlyTextarea` renders through `RawTextarea`, which is
 * Lexical with markdown transformers, so a value's markdown has already become semantic elements in
 * the DOM - there is nothing to convert and no markdown library to add.
 */
export const TextareaContentUtil = {
  /*
    These and `data-lexical-*` are Lexical's own bookkeeping and mean nothing
    outside the editor that wrote them. A `text/html` target would discard them
    anyway, but a copy that hands the markup over as literal text shows
    `<span data-lexical-text="true">Hello</span>`.
  */
  strippedAttributes: ['class', 'dir', 'style'],

  stripEditorAttributes({ element }: { element: Element }) {
    element.getAttributeNames().forEach(attribute => {
      if (
        TextareaContentUtil.strippedAttributes.includes(attribute) ||
        attribute.startsWith('data-lexical')
      ) {
        element.removeAttribute(attribute);
      }
    });
  },

  /*
    Lexical wraps every run of text in a span, which says nothing once the
    attributes above are stripped off it. One that still carries an attribute is
    doing something and is left alone.
  */
  unwrapBareSpans({ root }: { root: Element }) {
    root.querySelectorAll('span').forEach(span => {
      if (span.attributes.length > 0) return;

      /*
        `replaceWith(...span.childNodes)` is the obvious spelling and does not
        compile: the project's `lib` is `ES2022` without `DOM.Iterable`, so a
        `NodeList` has no iterator to spread. `Array.from` satisfies the
        compiler and then gets rewritten back into a spread by a lint autofix,
        so this avoids iterating the collection at all.
      */
      while (span.firstChild) {
        span.parentNode?.insertBefore(span.firstChild, span);
      }

      span.remove();
    });
  },

  /*
    Lexical's own marker rather than `[contenteditable]`, which a readonly
    editor renders as `"false"` - copying out of a readonly field is the whole
    point of this util.
  */
  editorSelector: '[data-lexical-editor]',

  /**
   * Whether there is anything to copy the formatting of, which is the same
   * question `editorClone` answers by returning null - asked without the clone,
   * so a menu can disable an option it cannot honour without walking the DOM to
   * find out.
   */
  hasEditor({ container }: { container: HTMLElement | null }) {
    return Boolean(
      container?.querySelector(TextareaContentUtil.editorSelector)
    );
  },

  /**
   * A **clone**, so the live editor is never touched - stripping attributes off
   * the real nodes would take Lexical's own markers with them and it would lose
   * track of its content.
   */
  editorClone({ container }: { container: HTMLElement | null }) {
    const editor = container?.querySelector(TextareaContentUtil.editorSelector);

    return editor ? (editor.cloneNode(true) as Element) : null;
  },

  paragraphTags: ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE'],
  lineTags: ['LI'],
  nestedListSelector: 'ul,ol',

  /**
   * The line breaks have to be put in by hand: `textContent` concatenates every
   * text node with nothing between them, and `innerText`, which turns block
   * boundaries into newlines for free, is banned by
   * `unicorn/prefer-dom-node-text-content`.
   *
   * A separator is appended *inside* each block rather than after it, because a
   * block's own subtree is what gets concatenated and a node placed after the
   * last block would sit outside what is being read.
   */
  toText({ container }: { container: HTMLElement | null }) {
    const clone = TextareaContentUtil.editorClone({ container });

    if (!clone) return null;

    clone.querySelectorAll('br').forEach(lineBreak => {
      lineBreak.replaceWith(document.createTextNode('\n'));
    });

    clone
      .querySelectorAll(
        /*
          Lowercased: the arrays are uppercase for the `tagName` comparison
          below, and jsdom - unlike a browser - does not fold the case of a CSS
          type selector, so the uppercase spelling matches nothing under test.
        */
        [...TextareaContentUtil.paragraphTags, ...TextareaContentUtil.lineTags]
          .join(',')
          .toLowerCase()
      )
      .forEach(block => {
        const separator = document.createTextNode(
          TextareaContentUtil.lineTags.includes(block.tagName) ? '\n' : '\n\n'
        );
        /*
          A list item holding a nested list is separated from it here rather
          than at its end: a separator appended after the sublist leaves the
          item's own text running straight into the first nested item.
        */
        const nestedList = block.querySelector(
          TextareaContentUtil.nestedListSelector
        );

        if (nestedList?.parentNode === block) {
          nestedList.before(separator);
        } else {
          block.append(separator);
        }
      });

    return clone.textContent.replaceAll(/\n{3,}/g, '\n\n').trim();
  },

  toHtml({ container }: { container: HTMLElement | null }) {
    const clone = TextareaContentUtil.editorClone({ container });

    if (!clone) return null;

    clone.querySelectorAll('*').forEach(element => {
      TextareaContentUtil.stripEditorAttributes({ element });
    });
    TextareaContentUtil.unwrapBareSpans({ root: clone });

    return clone.innerHTML;
  },
};
